import React from "react";
import { Box, Container, Grid, Typography, Button } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PlaceIcon from "@mui/icons-material/Place";
import { StoreThemeTokens, getStoreTheme } from "./themes/storeThemes";
import { CategoryItem, getStoreCategories } from "../../utils/categoryHelper";
import { getWhatsAppUrl } from "../../utils/whatsappHelper";
import { useNavigate } from "react-router-dom";
import { getWarrantyPageUrl } from "../../utils/warrantyHelper";

interface StoreFooterProps {
  storeData?: any;
  themeTokens?: StoreThemeTokens;
  onSelectCategory?: (category: string) => void;
  categories?: CategoryItem[];
}

const StoreFooter: React.FC<StoreFooterProps> = ({
  storeData,
  themeTokens: propTokens,
  onSelectCategory,
  categories: propCategories,
}) => {
  const navigate = useNavigate();
  const categoriesList = propCategories || getStoreCategories(storeData);
  const enabledCategories = categoriesList.filter((c) => c.enabled !== false);
  const themeTokens = propTokens || getStoreTheme(storeData?.theme);
  const storeName = storeData?.name || storeData?.header?.store_name || "RUDRIX";
  const tagline = storeData?.header?.tagline || "Elegance Meets Care";
  const description =
    storeData?.description ||
    storeData?.header?.description ||
    "Delivering premium personal wellness, salon-grade hair stylers, and checked electronics right to your doorstep across India.";
  const location = storeData?.address || storeData?.header?.location || "Thane, Maharashtra, India";
  const phone = storeData?.number || storeData?.header?.phone_number || "";

  const handleCategoryClick = (cat: string) => {
    if (onSelectCategory) {
      onSelectCategory(cat);
    }
    const section = document.getElementById("products-section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleWhatsAppChat = () => {
    if (phone) {
      const waUrl = getWhatsAppUrl(
        phone,
        `Hi ${storeName}, I need help with an order`
      );
      if (waUrl) window.open(waUrl, "_blank");
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: themeTokens.bgFooter,
        color: themeTokens.bgFooterText,
        pt: { xs: 7, md: 9 },
        pb: 5,
        borderTop: `1px solid rgba(255,255,255,0.08)`,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, sm: 5, lg: 6 }} sx={{ pb: 6, borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
          {/* Brand Info */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: themeTokens.fontSerif,
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  letterSpacing: "0.06em",
                  color: "#FFFFFF",
                  textTransform: "uppercase",
                }}
              >
                {storeName}
              </Typography>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: "#10b981",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "bold",
                }}
              >
                ✓
              </Box>
            </Box>

            {tagline && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: themeTokens.accent,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  fontSize: "0.72rem",
                  mb: 2,
                }}
              >
                {tagline}
              </Typography>
            )}

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.65)",
                fontSize: "0.85rem",
                lineHeight: 1.65,
                mb: 2.5,
                maxWidth: 360,
              }}
            >
              {description}
            </Typography>

            {location && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "rgba(255,255,255,0.7)" }}>
                <PlaceIcon sx={{ fontSize: 16, color: themeTokens.accent }} />
                <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
                  {location}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Quick Categories */}
          {enabledCategories.length > 0 && (
            <Grid item xs={6} sm={4} lg={2.5}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#FFFFFF",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  fontSize: "0.78rem",
                  mb: 2.5,
                }}
              >
                Categories
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {enabledCategories.map((cat) => (
                  <Box
                    component="li"
                    key={cat.id || cat.name}
                    onClick={() => handleCategoryClick(cat.name)}
                    sx={{
                      color: "rgba(255,255,255,0.65)",
                      fontSize: "0.82rem",
                      cursor: "pointer",
                      transition: "color 0.2s ease",
                      "&:hover": {
                        color: themeTokens.accent,
                      },
                    }}
                  >
                    {cat.name}
                  </Box>
                ))}
              </Box>
            </Grid>
          )}

          {/* Customer Care */}
          <Grid item xs={6} sm={4} lg={2.5}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#FFFFFF",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.78rem",
                mb: 2.5,
              }}
            >
              Customer Care
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[
                { label: "WhatsApp Direct Helpline", action: handleWhatsAppChat, highlight: true },
                { label: "All India Shipping Policy" },
                { label: "100% Quality Checked" },
                { label: "Easy Returns & Exchange" },
                {
                  label: "Warranty Registration",
                  action: () => {
                    const el = document.getElementById("warranty-registration");
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth" });
                    } else {
                      navigate(getWarrantyPageUrl(storeData?.url));
                    }
                  },
                  highlight: true,
                },
              ].map((item, idx) => (
                <Box
                  component="li"
                  key={idx}
                  onClick={item.action}
                  sx={{
                    color: item.highlight ? "#4ade80" : "rgba(255,255,255,0.65)",
                    fontSize: "0.82rem",
                    fontWeight: item.highlight ? 600 : 400,
                    cursor: item.action ? "pointer" : "default",
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: item.highlight ? "#86efac" : themeTokens.accent,
                    },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Direct Order via WhatsApp */}
          <Grid item xs={12} sm={4} lg={3}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#FFFFFF",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.78rem",
                mb: 2.5,
              }}
            >
              Direct Order
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.65)",
                fontSize: "0.82rem",
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              Order quickly without checkout hassles via our official WhatsApp line.
            </Typography>

            {phone && (
              <Button
                variant="contained"
                fullWidth
                onClick={handleWhatsAppChat}
                startIcon={<WhatsAppIcon />}
                sx={{
                  bgcolor: "#16a34a",
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  borderRadius: 2.5,
                  py: 1.2,
                  "&:hover": {
                    bgcolor: "#15803d",
                  },
                }}
              >
                Chat on WhatsApp
              </Button>
            )}
          </Grid>
        </Grid>

        {/* Footer Bottom Row */}
        <Box
          sx={{
            pt: 4,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            fontSize: "0.78rem",
            color: "rgba(255,255,255,0.45)",
          }}
        >
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
            © {new Date().getFullYear()} {storeName} — {tagline}. All Rights Reserved.
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", cursor: "pointer", "&:hover": { color: "#FFFFFF" } }}>
              Privacy Policy
            </Typography>
            <span>•</span>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", cursor: "pointer", "&:hover": { color: "#FFFFFF" } }}>
              Terms of Service
            </Typography>
            {location && (
              <>
                <span>•</span>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  {location}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default StoreFooter;
